import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import * as moment from "moment";
import {
    CAR_CERTIFICATE_FILENAME,
    CAR_MTA_CONFIRMATION_PACK,
    CAR_WELCOME_PACK,
    CAR_CANCELLATION_EMAIL_TITLE,
    CAR_CHARGEBACK_CANCELLATION,
    CAR_CHARGEBACK_COVER,
    CAR_CHARGEBACK_NO_COVER,
    CAR_RENEWAL_ACCEPTANCE,
    CAR_RENEWAL_CLAIMS_IMPACT_PACK,
    CAR_RENEWAL_INVITE_PACK,
    CAR_RENEWAL_NCD_PROOF,
    CommonHelpers,
    Document,
    FREETEXT_DOCUMENT,
    NON_PRINTABLE_DOCUMENTS,
    Policy,
    POLICY_STATUS_PENDING_RENEWAL,
    productHelper,
    Renewal,
    ProductsEnum
} from "@ferry/journey-services";
import {FalconNavigatorHeader, Footer} from "@ferry/portal-core";
import {lazyInject} from "@athena-portal/core";
import FalconHeader from "../../../../components/navigation/FalconHeader";
import {downloadDocument, trimFileExtension} from "../../../../CommonFunctions";
import {FalconAccordion} from "../../../../components/FalconAccordion";
import {mapCommunicationTitles, mapDocumentTitles} from "../../../../utils/DataMappers";
import {RenewalDocumentsBanner} from "../../../../components/banners/RenewalDocumentsBanner";
import {PostRequestedDocumentsButton} from "./PostRequestedDocumentsButton";
import {DocumentListModal} from "@ferry/plugin-ferry-journey";
import {Types} from "../../../../Types";
import type {ICustomerPortalService} from "../../../../services/ICustomerPortalService";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    policy?: Policy;
    carsDocuments: Document[];
    archiveDocuments: Document[];
    archivedDocumentsElementsByDate: React.ReactNode[];
    carsRenewalDocuments: Document[];
    communications: Document[];
    direction: string;
    active: boolean;
    isWhite: boolean;
    day: number;
    isDocumentDownloaded: boolean;
    receiveByPostChecked: boolean | null;
    renewalProposal?: Renewal;
    showLoading: boolean;
}

@authorize
export class DocumentsPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private readonly FILE_PATH: string = "path";
    private readonly FILE_NAME: string = "fileName";
    private readonly FILE_ID: string = "id";
    private readonly FILE_LABEL: string = "label";

    constructor(props: Readonly<IWithRouterProps<IParams>>) {
        super(props);
        this.state = {
            direction: 'asc',
            carsDocuments: [],
            carsRenewalDocuments: [],
            archiveDocuments: [],
            archivedDocumentsElementsByDate: [],
            communications: [],
            active: false,
            isWhite: true,
            day: 0,
            isDocumentDownloaded: true,
            receiveByPostChecked: null,
            renewalProposal: undefined,
            showLoading: true
        };

        this.sortByDate = this.sortByDate.bind(this);
        this.renderDocumentsByPost = this.renderDocumentsByPost.bind(this);
        this.showMore = this.showMore.bind(this);
        this.createPDFMap = this.createPDFMap.bind(this);

    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Policy documents");

        const policyNumber: string = this.props.params.policyNumber;

        this.customerPortalService.getPolicy(policyNumber).subscribe((policy: Policy) => {
            this.customerPortalService.getRenewalProposal(policyNumber).subscribe((renewalProposal: Renewal) => {
                this.customerPortalService.getAllDocuments().subscribe((documents: Document[]) => {
                    this.fetchCommunications(policy, documents);
                    this.fetchDocuments(policy, renewalProposal, documents);
                })

                this.setState({renewalProposal});
            });

            this.setState({
                policy,
                showLoading: false
            })
        });
    }

    public render() {
        const hasPendingRenewal: boolean = this.state.renewalProposal != null && this.state.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;
        const isRenewed: boolean | undefined = this.state.policy?.annualPolicyInfo?.renewedPolicy;

        const isDriveAwayPolicy = ProductsEnum.DRIVE_AWAY === this.state.policy?.products[0];
        const backButtonLink = `/dashboard/${isDriveAwayPolicy ? "driveAway" : "annualMotor"}/${this.state.policy?.policyNumber}`;

        return (
            <React.Fragment>
                <FalconHeader/>

                <div className="plugin">
                    <FalconNavigatorHeader
                        back={this.state.policy && backButtonLink}
                        title="Documents"
                    />
                    {!this.state.showLoading && <div className="documents-page">
                        {hasPendingRenewal && <RenewalDocumentsBanner/>}
                        {hasPendingRenewal && this.renderRenewalDocumentsPanel()}
                        {hasPendingRenewal && this.renderExistingDocumentsPanel(false)}
                        {isRenewed && this.renderExistingDocumentsPanel(true)}
                        {!hasPendingRenewal && !isRenewed && this.renderDocumentsPanel()}

                        {this.renderDocumentsArchivePanel()}
                        {this.renderDocumentsByPost()}
                    </div>}
                    {this.state.policy && this.pluginTemplates.renderBackLink("Back", false, backButtonLink)}
                </div>
                <Overlay show={this.state.showLoading}/>
                <Footer/>
            </React.Fragment>
        );
    }

    private renderDocumentsArchivePanel() {
        if (this.state.archiveDocuments && this.state.archiveDocuments.length !== 0) {
            let agreementVersionIndex: any;
            this.state.archiveDocuments.find(doc => {
                doc.tags.find((tag, index) => {
                    if (tag.replace(/[0-9]/g, '') === "agreement_version_") {
                        return agreementVersionIndex = index;
                    } else {
                        return undefined;
                    }
                })
            });
            if (agreementVersionIndex !== undefined) {
                this.state.archiveDocuments.sort((a, b) => b.tags[agreementVersionIndex].localeCompare(a.tags[agreementVersionIndex]) || moment(b.date).diff(a.date));
            } else {
                this.state.archiveDocuments.sort((a, b) => moment(b.date).diff(a.date));
            }
            if (!(this.state.policy?.annualPolicyInfo?.renewedPolicy)) {
                return (
                    <div className="widget documents-widget active-policy-documents-widget archive panel">
                        <p className="active-policy-documents-title font-weight-bold text-center fs-18">Archived policy documents</p>
                        <ul className="list-unstyled">
                            <FalconAccordion toggleHidden={ <div className="toggle btn btn-white-dashboard mb-0 mt-0">
                                                            <span>Show all documents</span>
                                                            <i className={"fas fa-chevron-down glyph-margin"} />
                                                            </div>}
                                             toggleShown={  <div className="toggle btn btn-white-dashboard mb-0 mt-0">
                                                            <span>Show less documents</span>
                                                            <i className={"fas fa-chevron-up glyph-margin"} />
                                                            </div>}>
                                {this.state.archivedDocumentsElementsByDate.length > 0 && this.state.archivedDocumentsElementsByDate}
                            </FalconAccordion>
                        </ul>
                    </div>
                );
            } else {
                return (
                    <div className="widget documents-widget documents-archived-widget panel">
                        <ul className="list-unstyled">
                            <FalconAccordion
                                togglePositionTop={true}
                                toggleShown={<div className="documents-archived-title toggle d-flex justify-content-between align-items-center">
                                    <span/>
                                    <span
                                        className="font-weight-bold text-center fs-18">Archived policy documents</span>
                                    <span className="toggle-icon collapse-icon"/></div>}
                                toggleHidden={<div className="documents-archived-title toggle d-flex justify-content-between align-items-center">
                                    <span/>
                                    <span
                                        className="font-weight-bold text-center fs-18">Archived policy documents</span>
                                    <span className="toggle-icon expand-icon"/></div>}>
                                {this.renderDocuments(this.state.archiveDocuments)}
                            </FalconAccordion>
                        </ul>
                    </div>
                );
            }
        } else {
            return <></>;
        }
    }

    private renderDocumentsByPost() {
        return (
            <div className="receive-by-post">
                <p className="receive-by-post-title font-weight-bold fs-18">Receive my policy documents by post</p>
                <p className="receive-by-post-description">We've gone paperless; we care about the environment and it's better for everyone! But if you wish,
                    you can request below to have your documents posted to you to the address listed on your policy</p>
                {(this.state.carsDocuments || this.state.carsRenewalDocuments) && this.state.policy &&
                    <PostRequestedDocumentsButton
                        policy={this.state.policy}
                        allDocuments={CommonHelpers.getPrintableDocuments(this.state.carsDocuments.concat(this.state.carsRenewalDocuments), NON_PRINTABLE_DOCUMENTS)}
                    />
                }
            </div>
        )
    }

    private renderDocumentsPanel() {
        if (this.state.policy){
            return (
                <div className="widget documents-widget active-policy-documents-widget panel">
                    <p className="active-policy-documents-title font-weight-bold text-center fs-18">Existing policy documents</p>
                    <ul className="list-unstyled">
                        {this.state.communications.length > 0 && this.renderDocuments(this.state.communications)}
                        {this.renderDocuments(this.state.carsDocuments)}
                        <FalconAccordion toggleHidden={<div className="toggle">+ Show more</div>}
                                         toggleShown={<div className="toggle">- Show less</div>}>
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.policy.products[0])}
                                policyDate={new Date(this.state.policy.interaction.factors.originalQuoteDate)}
                                isMLEVisible={this.state.policy.interaction.optionalExtras.motorLegalExpenses}
                                isAccordion={false}
                                coverTier={this.state.policy.interaction.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
        } else {
            return <></>;
        }
    }

    private renderExistingDocumentsPanel(isShown: boolean) {
        if(this.state.policy) {
            const isRenewed: boolean | undefined = this.state.policy?.annualPolicyInfo?.renewedPolicy;
            const cssClass: string = isRenewed ? "existing-documents-widget-pending-renewal" : "existing-documents-widget";

            return (
                <div className={`widget documents-widget panel ${cssClass}`}>
                    <ul className="list-unstyled">
                        <FalconAccordion
                            togglePositionTop={true}
                            isShown={isShown}
                            toggleShown={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-collapsed"><span/>
                                <span className="font-weight-bold text-center fs-18">Existing policy documents</span>
                                <span className="toggle-icon collapse-icon"/></div>}
                            toggleHidden={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-expanded"><span/>
                                <span className="font-weight-bold text-center fs-18">Existing policy documents</span>
                                <span className="toggle-icon expand-icon"/></div>}>
                            {this.state.communications.length > 0 && this.renderDocuments(this.state.communications)}
                            {this.renderDocuments(this.state.carsDocuments)}
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.policy?.products[0])}
                                policyDate={new Date(this.state.policy.interaction.factors.originalQuoteDate)}
                                isMLEVisible={this.state.policy.interaction.optionalExtras.motorLegalExpenses}
                                isAccordion={false}
                                coverTier={this.state.policy.interaction.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
       } else {
           return <></>;
       }
    }

    private renderRenewalDocumentsPanel() {
        if (this.state.policy && this.state.renewalProposal){
            return (
                <div className="widget documents-widget renewal-documents-widget panel">
                    <ul className="list-unstyled">
                        <FalconAccordion isShown={true} togglePositionTop={true} toggleHidden={<div
                            className="renewal-documents-title toggle d-flex justify-content-between align-items-center accordion-collapsed"><span/><span
                            className="font-weight-bold text-center fs-18">Renewal policy documents</span><span
                            className="toggle-icon expand-icon"/></div>}
                                         toggleShown={<div
                                             className="renewal-documents-title toggle d-flex justify-content-between align-items-center accordion-expanded">
                                             <span/><span
                                             className="font-weight-bold text-center fs-18">Renewal policy documents</span><span
                                             className="toggle-icon collapse-icon"/></div>}>
                            {this.state.communications.length > 0 && this.renderDocuments(this.state.communications)}
                            {this.renderDocuments(this.state.carsRenewalDocuments)}
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.policy?.products[0])}
                                policyDate={new Date(this.state.renewalProposal.renewalDetails.periodStartDate)}
                                isMLEVisible={this.state.policy.interaction.optionalExtras.motorLegalExpenses}
                                isAccordion={true}
                                coverTier={this.state.policy.interaction.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
        } else {
            return <></>;
        }
    }

    private renderDocuments(documents: Document[]) {
        return documents.map((document: Document) => (
            <li className="media mb-3" key={document.id}>
                <div className="media-body">
                    <div className="row height-0">
                        <div className="col-1"/>
                        <div className="col-11">
                            <div className="dashed-10 ml-3 mr-3"/>
                        </div>
                    </div>
                    <a className="document-download-link" onClick={this.downloadDocument(document)}
                       id={trimFileExtension(document.name)}>
                        <img className="pdf-icon active-icon" alt="Document download icon"
                             src="/img/pdf-icon.png"/>
                        <h5 className="pt-2 document-name">{document.title}</h5>
                    </a>
                </div>
            </li>
        ));
    }

    private fetchDocuments(policy?: Policy, renewalProposal?: Renewal, documents?: Document[]): void {
        if (policy && documents) {
            const carsRenewalDocuments: Document[] = [];
            const carsDocuments: Document[] = [];
            const archiveDocuments: Document[] = [];
            const ncdProofDocs: Document[] = [];
            let hasActivePolicies: boolean = false;

            if (renewalProposal) {
                const renewalPdfs = this.getRenewalDocuments(documents, renewalProposal);
                carsRenewalDocuments.push(...mapDocumentTitles(renewalPdfs.reverse(), policy));
            }

            const allPolicyDocs = documents.filter(doc => {
                return doc.tags.filter(d => d === ("agreement_" + policy.policyNumber)).length > 0
            });

            const currentPolicyVersionDocs = allPolicyDocs.filter(doc => {
                return doc.tags.filter(d => d === ("agreement_version_" + policy.interaction.agreementVersion)).length > 0
            });

            const documentsToBeListed = currentPolicyVersionDocs.filter(doc =>
                doc.name.includes(CAR_CERTIFICATE_FILENAME)
                || doc.name.includes(CAR_RENEWAL_ACCEPTANCE)
                || doc.name.includes(CAR_WELCOME_PACK)
                || doc.name.includes(CAR_MTA_CONFIRMATION_PACK));

            if (!hasActivePolicies && !productHelper.isActivePolicy(policy)) {
                const policyNcdProofDoc = allPolicyDocs.filter(doc => doc.name.includes(CAR_RENEWAL_NCD_PROOF + ".pdf"));

                if (policyNcdProofDoc) {
                    ncdProofDocs.push(...mapDocumentTitles(policyNcdProofDoc, policy));
                }
            } else {
                hasActivePolicies = true;
            }

            const mappedCurrentDocuments = mapDocumentTitles(documentsToBeListed, policy);
            carsDocuments.push(...mappedCurrentDocuments);

            const documentsToBeListedWithoutAgreementVersion = allPolicyDocs.filter(doc =>
                this.shouldDocumentBeListedWithoutAgreementVersion(doc, policy.interaction.agreementVersion)
            );

            archiveDocuments.push(...mapDocumentTitles(documentsToBeListedWithoutAgreementVersion.reverse(), policy));

            if (!hasActivePolicies && ncdProofDocs.length > 0 ) {
                let latestNcdProofDoc = ncdProofDocs.sort((a, b) => moment(b.date).diff(moment(a.date)))[0];
                carsDocuments.push(latestNcdProofDoc);
            }

            const archivedDocumentsElementsByDate = this.getArchivedDocumentsByDate(archiveDocuments, false);

            this.setState({
                carsRenewalDocuments,
                carsDocuments,
                archiveDocuments,
                archivedDocumentsElementsByDate
            })
        }
    };

    private fetchCommunications(policy: Policy, documents?: Document[]): void {
        if (policy && documents) {
            const communications: Document[] = [];
            const mtcPolicy: boolean = /mtc/i.exec(policy.status) !== null;

            if (mtcPolicy) {
                const mtcDocs = documents.filter(doc => doc.name.includes(CAR_CANCELLATION_EMAIL_TITLE));
                communications.push(...mapCommunicationTitles(mtcDocs, policy));
            }
            this.setState({communications});
        }
    }

    private readonly downloadDocument = (doc: Document) => (): void => {
        this.setState({isDocumentDownloaded: false});
        this.customerPortalService.downloadDocument(doc.id).subscribe(
            (documentContent: ArrayBuffer) => {
                if (documentContent) {
                    downloadDocument(documentContent, doc.downloadName);
                }
                this.setState({isDocumentDownloaded: true});
            }
        );
    };

    private sortByDate(): void {
        const sortedDates = this.state.direction === 'asc' ?
            this.state.archiveDocuments!.sort((a, b) => moment(a.date).diff(b.date))
            :
            this.state.archiveDocuments!.sort((a, b) => moment(b.date).diff(a.date));
        this.setState({
            archiveDocuments: sortedDates,
            direction: this.state.direction === 'asc' ? 'desc' : 'asc',
            active: true
        });
    }

    private showMore(): void {
        this.setState({
            archivedDocumentsElementsByDate: this.getArchivedDocumentsByDate(this.state.archiveDocuments, true)
        })
    }

    private createPDFMap(path: string, fileName: string, id: string, label: string): Map<string, string> {
        return new Map()
            .set(this.FILE_PATH, path)
            .set(this.FILE_NAME, fileName)
            .set(this.FILE_ID, id)
            .set(this.FILE_LABEL, label);
    }

    private getArchivedDocumentsByDate(archivedDocuments: Document[], showHistory: boolean) {
        let documents: React.ReactNode[];
        let day: number = 0;
        if (archivedDocuments && archivedDocuments.length !== 0) {
            let previousDate: string | null = "";
            let isWhite: boolean = true;
            documents = archivedDocuments.map((document, index) => {
                const {date, name, title} = document;
                const displayTitle = title || name;

                if (PluginTemplates.convertDate(date, "DD MMMM YYYY") === previousDate) {
                    const history: string = day >= 4 ? "display-none" : "";
                    return (
                        <li key={index} className={`mb-3 ${history} ${showHistory ? "display-block" : ""}`}>
                            <a className="btn btn-text d-flex text-align-left align-i-center"
                               onClick={this.downloadDocument(document)}
                               id={trimFileExtension(name)}>
                                <span className="file-icon"/>
                                <p className="mb-0">
                                    {displayTitle}
                                </p>
                            </a>
                        </li>
                    )
                } else {
                    previousDate = PluginTemplates.convertDate(date, "DD MMMM YYYY");
                    isWhite = !isWhite;
                    day += 1;
                    const history: string = day >= 4 ? "display-none" : "";
                    return (
                        <li key={index} className={`mb-3 ${history} ${showHistory ? "display-block" : ""}`}>
                            <a className="btn btn-text d-flex text-align-left align-i-center"
                               onClick={this.downloadDocument(document)}
                               id={trimFileExtension(name)}>
                                <span className="file-icon"/>
                                <p className="mb-0">
                                    {displayTitle}
                                </p>
                            </a>
                        </li>
                    )
                }
            });
        } else {
            return [];
        }
        this.setState({day});
        return documents;
    }

    private getRenewalDocuments(documents: Document[], renewalProposal: Renewal): Document[] {
        const renewalDocs = documents.filter(doc => {
            return doc.tags.filter(d => d === ("renewal_" + renewalProposal._id) || d === ("renewal_link_" + renewalProposal._id)).length > 0;
        });

        const renewalPdfs = renewalDocs.filter(doc =>
            doc.name.includes(CAR_RENEWAL_CLAIMS_IMPACT_PACK)
            || doc.name.includes(CAR_RENEWAL_NCD_PROOF));

        const invitePdfs = renewalDocs.filter(doc =>
            doc.name.includes(CAR_RENEWAL_INVITE_PACK));

        // sort by date from latest and get latest generated renewal invite to avoid duplicates in documents view
        if (invitePdfs.length > 0) {
            this.sortDocuments(invitePdfs);
            let latestInvitePdf = invitePdfs[0];
            renewalPdfs.push(latestInvitePdf);
        }

        return renewalPdfs;
    }

    private sortDocuments(documents: Document[]) {
        documents.sort((a, b) => moment(b.date).diff(moment(a.date)));
    }

    private shouldDocumentBeListedWithoutAgreementVersion(doc: Document, agreementVersion: string): boolean {
        return (doc.name.includes(CAR_CERTIFICATE_FILENAME) && !doc.tags.includes(("agreement_version_" + agreementVersion)))
            || (doc.name.includes(CAR_WELCOME_PACK) && !doc.tags.includes(("agreement_version_" + agreementVersion)))
            || (doc.name.includes(CAR_MTA_CONFIRMATION_PACK) && !doc.tags.includes(("agreement_version_" + agreementVersion)))
            || doc.name.includes(CAR_CHARGEBACK_COVER)
            || doc.name.includes(CAR_CHARGEBACK_NO_COVER)
            || doc.name.includes(CAR_CHARGEBACK_CANCELLATION)
            || doc.name.includes(FREETEXT_DOCUMENT)
    }

}

export const DocumentsPage = withRouter<{}, IParams>(DocumentsPagePlain)
